import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  annotation as csAnnotation,
  Enums as ToolEnums,
} from '@cornerstonejs/tools';
import { eventTarget } from '@cornerstonejs/core';
import {
  selectAnnotationToMeasurement,
  selectSelectedAnnotationUID,
} from '@/state/slices/measurementSession';
import { useGetWorksheetCanonicalMeasurementsQuery } from '@/state/slices/options';
import { darkColors } from '@/theme/colors';
import type {
  SpectralDopplerValues,
  SpectralDopplerToolVariant,
} from '@/features/study-detail/Viewer/tools/SpectralDoppler';
import { formatAnnotationValue } from '../utils/formatAnnotationValue';
import styles from './SelectedAnnotationLabel.module.css';

interface Props {
  dicomUuid: string | undefined;
  /** The pane's stack imageId (`web:…`) when it shows a still; null for videos.
   *  Doppler/linear stills anchor to this instead of a `video:` id, so it's how
   *  we tell whether a selected still annotation belongs to this pane. */
  stackImageId?: string | null;
}

/** Extract the DICOM uuid from a Cornerstone referencedImageId like
 *  `video:{dicomUuid}/frames/N`. Returns null for non-video (stack) ids. */
function extractDicomUuid(referencedImageId?: string): string | null {
  if (!referencedImageId) return null;
  const match = referencedImageId.match(/video:([^/]+)/);
  return match?.[1] ?? null;
}

/** True when the selected annotation belongs to this pane. Video annotations
 *  carry `video:<dicomUuid>/frames/N`; stills carry the stack imageId verbatim,
 *  which has no dicomUuid to parse — so match those against the pane's own. */
function annotationBelongsToPane(
  referencedImageId: string | undefined,
  dicomUuid: string,
  stackImageId: string | null | undefined
): boolean {
  if (referencedImageId?.startsWith('video:')) {
    return extractDicomUuid(referencedImageId) === dicomUuid;
  }
  return !!stackImageId && referencedImageId === stackImageId;
}

/**
 * Header chip for the currently-selected annotation, rendered in the pane's
 * topRightOverlay. Only visible when the selected annotation belongs to this
 * pane's DICOM. Source-color background (mint for AI, yellow for manual).
 */
export function SelectedAnnotationLabel({ dicomUuid, stackImageId }: Props) {
  const selectedUID = useSelector(selectSelectedAnnotationUID);
  const annotationToMeasurement = useSelector(selectAnnotationToMeasurement);
  const { data: canonicalMeasurements } =
    useGetWorksheetCanonicalMeasurementsQuery();
  // Bump on ANNOTATION_MODIFIED so drag-to-resize updates the value live.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!selectedUID) return undefined;
    // Tick on any ANNOTATION_MODIFIED — cheaper to always re-render this
    // small chip than to reason about event-detail variance across
    // Cornerstone versions/tools.
    const handler = () => setTick((t) => t + 1);
    eventTarget.addEventListener(ToolEnums.Events.ANNOTATION_MODIFIED, handler);
    return () => {
      eventTarget.removeEventListener(
        ToolEnums.Events.ANNOTATION_MODIFIED,
        handler
      );
    };
  }, [selectedUID]);

  if (!selectedUID || !dicomUuid) return null;

  const ann = csAnnotation.state.getAnnotation(selectedUID) as
    | {
        data?: {
          abbreviation?: string;
          isAI?: boolean;
          cachedStats?: Record<string, { length?: number; area?: number }>;
          simpsonStats?: {
            volume?: number;
            volumeUnit?: string;
            longAxisLength?: number;
            longAxisUnit?: string;
          };
          spectralValues?: SpectralDopplerValues;
          spectralToolVariant?: SpectralDopplerToolVariant;
        };
        metadata?: { referencedImageId?: string };
      }
    | undefined;
  if (!ann) return null;

  if (
    !annotationBelongsToPane(
      ann.metadata?.referencedImageId,
      dicomUuid,
      stackImageId
    )
  ) {
    return null;
  }

  const abbr = ann.data?.abbreviation ?? '';
  const isAI = !!ann.data?.isAI;

  // Primary value by annotation family: Simpson volume trace → volume (mL),
  // area trace → area (cm²), linear → length (cm).
  const canonicalUuid = annotationToMeasurement[selectedUID];
  const canonical = canonicalUuid
    ? canonicalMeasurements?.find((c) => c.uuid === canonicalUuid)
    : undefined;
  const valueText = formatAnnotationValue(ann.data, canonical);

  if (!abbr && !valueText) return null;

  const bg = isAI
    ? darkColors.annotationAiActive
    : darkColors.annotationManualActive;
  const fg = darkColors.annotationChipTextActive;

  return (
    <div
      className={styles.card}
      data-tick={tick}
      style={{ background: bg, color: fg }}
    >
      <div className={styles.main}>
        {abbr && <span className={styles.abbr}>{abbr}</span>}
        {valueText && <span className={styles.value}>{valueText}</span>}
      </div>
    </div>
  );
}
